/**
 * 参考轴创建模式。
 */
export enum AxisMode {
    /** 一直线/边线/轴。 */
    LineEdgeAxis = 'line-edge-axis',
    /** 两平面。 */
    TwoPlanes = 'two-planes',
    /** 两点/顶点。 */
    TwoPoints = 'two-points',
    /** 圆柱/圆锥面。 */
    CylOrConeFace = 'cyl-or-cone-face',
    /** 点和面/基准面。 */
    PointAndFaceOrPlane = 'point-and-face-or-plane',
}

/**
 * 基准轴可解析的解析曲面类型。
 */
export enum AxisSurfaceType {
    /** 圆柱曲面。 */
    Cylindrical = 'cylindrical',
    /** 圆锥曲面。 */
    Conical = 'conical',
}

/**
 * 参考面约束类型。
 */
export enum PlaneConstraintType {
    /** 自动。 */
    Auto = 'auto',
    /** 重合。 */
    Coincident = 'coincident',
    /** 平行。 */
    Parallel = 'parallel',
    /** 垂直。 */
    Perpendicular = 'perpendicular',
    /** 法向。 */
    Normal = 'normal',
    /** 两面夹角。 */
    Angle = 'angle',
    /** 偏移距离。 */
    Distance = 'distance',
    /** 两侧对称。 */
    MidPlane = 'midPlane',
    /** 相切。 */
    Tangent = 'tangent',
    /** 在曲面上。 */
    OnSurface = 'onSurface',
    /** 投影。 */
    Project = 'project',
}

/**
 * 参考面投影类型。
 */
export enum PlaneProjectionType {
    /** 最近位置。 */
    NearestOnSurface = 'nearestOnSurface',
    /** 沿草图法线。 */
    AlongSketchNormal = 'alongSketchNormal',
}

/**
 * 参考面求解状态。
 */
export enum PlaneSolveStatus {
    /** 欠定义。 */
    UnderDefined = 'underDefined',
    /** 完全定义。 */
    FullyDefined = 'fullyDefined',
    /** 过定义。 */
    OverDefined = 'overDefined',
    /** 不一致。 */
    Inconsistent = 'inconsistent',
    /** 无解。 */
    NoSolution = 'no-solution',
}


/**
 * 参考面槽位角色。
 */
export enum PlaneReferenceRole {
    /** 第一参考。 */
    First = 'first',
    /** 第二参考。 */
    Second = 'second',
    /** 第三参考。 */
    Third = 'third',
}

/**
 * 参考特征命令生命周期。
 */
export enum ReferenceCommandLifecycle {
    /** 命令已关闭。 */
    Closed = 'closed',
    /** 命令正在打开并接收交互输入。 */
    Open = 'open',
}

/**
 * 参考特征命令模式来源。
 */
export enum ReferenceCommandModeSource {
    /** 模式由当前选择自动推断。 */
    AutoInferred = 'autoInferred',
    /** 模式由用户手动锁定。 */
    ManualLocked = 'manualLocked',
}

/**
 * 参考特征选择完整度状态。
 */
export enum ReferenceSelectionState {
    /** 尚未选择任何有效引用。 */
    Empty = 'empty',
    /** 已选择部分引用但不足以求解。 */
    Partial = 'partial',
    /** 引用已满足当前方法的求解要求。 */
    Complete = 'complete',
    /** 引用组合非法或超出槽位约束。 */
    Invalid = 'invalid',
}

/**
 * 参考特征求解过程状态。
 */
export enum ReferenceSolveState {
    /** 未开始求解或当前输入不足。 */
    Idle = 'idle',
    /** 正在执行预览求解。 */
    Solving = 'solving',
    /** 预览结果可提交。 */
    Previewable = 'previewable',
    /** 存在多解，需要用户消歧。 */
    Ambiguous = 'ambiguous',
    /** 求解失败。 */
    Error = 'error',
}

/**
 * 参考特征诊断严重级别。
 */
export enum ReferenceDiagnosticSeverity {
    /** 信息提示。 */
    Info = 'info',
    /** 可恢复告警。 */
    Warning = 'warning',
    /** 阻塞性错误。 */
    Error = 'error',
}

/**
 * 参考特征坐标系类型。
 */
export enum ReferenceCoordinateFrame {
    /** 零件坐标系。 */
    Part = 'part',
    /** 装配体全局坐标系。 */
    Assembly = 'assembly',
    /** 组件局部坐标系。 */
    ComponentLocal = 'component-local',
}

/**
 * 参考模块统一语义实体类型。
 */
export enum ReferenceEntitySemanticType {
    /** 特征点。 */
    FeaturePoint = 'featurePoint',
    /** 特征曲线。 */
    FeatureCurve = 'featureCurve',
    /** 特征线。 */
    FeatureLine = 'featureLine',
    /** 特征面。 */
    FeatureFace = 'featureFace',
    /** 特征曲面。 */
    FeatureSurface = 'featureSurface',
    /** 基准点。 */
    DatumPoint = 'datumPoint',
    /** 基准面。 */
    DatumPlane = 'datumPlane',
    /** 基准轴。 */
    DatumAxis = 'datumAxis',
}

/**
 * 参考面求解容差策略。
 */
export interface PlaneTolerancePolicy {
    /** 线性容差。 */
    linearEpsilon: number;
    /** 角度容差。 */
    angularEpsilon: number;
    /** 投影容差。 */
    projectionEpsilon: number;
}

/**
 * 默认参考面求解容差。
 */
export const DEFAULT_PLANE_TOLERANCE: PlaneTolerancePolicy = {
    linearEpsilon: 1e-6,
    angularEpsilon: 1e-6,
    projectionEpsilon: 1e-6,
};
